/**
 * @file BlendCalculator.js
 * @version 1.14.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-10-16
 * @description The Calculator tab: build a blend, watch its analysis and cost
 *              move as you type.
 *
 *              Everything computes locally from the mirrored blendMath, so
 *              typing is instant — no round-trip per keystroke. The server
 *              recomputes authoritatively for anything saved, quoted or
 *              printed; nothing here sets a price on its own.
 *
 *              The draft persists across navigation on purpose (no autoReset).
 *              It is a scratchpad, and losing a half-built blend by clicking
 *              through to Formulations would be the first complaint.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import usePersistedState from '../hooks/usePersistedState';
import useBlendDraft from '../hooks/useBlendDraft';
import * as blendMath from '../utils/blendMath';
import BlendGrid from './BlendGrid';
import BlendAllocationBar from './BlendAllocationBar';
import BlendSummary from './BlendSummary';
import BlendDetailsPanel from './BlendDetailsPanel';
import BlendWorkBar, { BLANK_COMPARE } from './BlendWorkBar';
import BlendBriefModal from './BlendBriefModal';
import { blendReferenceData, periodOptionsFrom } from '../utils/blendReferenceData';
import BlendPasteModal from './BlendPasteModal';
import BlendConvertModal from './BlendConvertModal';
import BlendExportModal from './BlendExportModal';
import BlendSaveModal from './BlendSaveModal';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import './SearchLayout.css';
import './TableStyles.css';
import { PROSPECT } from '../utils/blendCustomer';
import { badgeOf } from '../utils/permissions';
import { shortcutLabel } from '../utils/modifierKey';
import { LAPTOP_QUERY } from '../utils/displaySize';
import './BlendCalculator.css';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * A stable fingerprint of what a save would send, for the unsaved-changes badge.
 *
 * Keys are sorted because object order is not guaranteed across an edit, and a
 * reordered but identical recipe reading as "changed" would make the badge
 * meaningless. Quantities are normalised to numbers so "18" and 18 agree.
 */


const snapshotOf = (values, details) => JSON.stringify({
  v: Object.keys(values || {}).sort().map((k) => [k, Number(values[k]) || 0]),
  d: [details?.customerId || '', details?.deliveryAddressLine || '', details?.deliveryTerms || '',
      details?.packaging || '', details?.tonnesContracted || '', details?.periodOfContract || '',
      details?.haulageBandKey || '', details?.notes || '', details?.salePrice || '',
      details?.salePriceBasis || '', details?.salePriceType || '', details?.repId || '',
      [...(details?.priceColumnKeys || [])].sort().join('|')]
});


// A laptop display (utils/displaySize — one breakpoint for the whole client):
// the width at which the customer details become a pull-out drawer. Phones
// never get here (the calculator is desktop-only for a rep).

export default function BlendCalculator({ user, loadBlend, onLoaded }) {
  const [asOf, setAsOf] = usePersistedState('blendCalculator.asOf', today());
  const [priceColumnKey, setPriceColumnKey] = usePersistedState('blendCalculator.priceColumn', null);
  const [specId, setSpecId] = usePersistedState('blendCalculator.specId', null);
  const [showAll, setShowAll] = usePersistedState('blendCalculator.showAll', false);
  const [stored, setStored] = usePersistedState('blendCalculator.draft', { values: {}, locked: [] });

  const [book, setBook] = useState(null);
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paste, setPaste] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [savingBlend, setSavingBlend] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // The saved blend currently open in the grid, if any — {id, name, blend_type}.
  const [openBlend, setOpenBlend] = usePersistedState('blendCalculator.openBlend', null);

  // Who the blend is for and how it is quoted. These used to be collected in
  // the export modal, which meant a saved blend did not know its own customer
  // until somebody exported it.
  const [details, setDetails] = usePersistedState('blendCalculator.details', {
    customerId: '', deliveryAddressLine: '', priceColumnKeys: [], haulageBandKey: '',
    periodOfContract: '', packaging: '', deliveryTerms: '', tonnesContracted: '',
    salePrice: '', salePriceBasis: '', salePriceType: '', notes: '', repId: ''
  });
  const [notifyFor, setNotifyFor] = useState(null);
  /*
   * Clearing the bench is one action, not two.
   *
   * "New" and "Clear" did almost the same thing from two different places —
   * the header emptied the grid and left the loaded blend attached, the work
   * bar did the full reset. One button, the fuller behaviour, and confirmed:
   * it throws away work that is not saved, and undo does not survive it.
   */
  const [confirmReset, setConfirmReset] = useState(false);
  const [briefing, setBriefing] = useState(false);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);
  // How the unlocked materials give way when one is dragged or the blend is
  // balanced. A working preference, so it persists.
  // Read-only now the balance control that set it is gone; the stored
  // preference is still honoured for anyone who has one.
  const [redistributeMode] = usePersistedState('blendCalculator.redistribute', 'proportional');
  // The formulation this blend was started from, if any — {formulation_id,
  // item_code, item_name}. Shown in the header so the lineage is not a mystery.
  const [openFormulation, setOpenFormulation] = usePersistedState('blendCalculator.openFormulation', null);
  // What the recipe looked like when it was last saved or loaded, so the header
  // can say whether there are unsaved changes rather than leaving people to
  // guess.
  const [savedSnapshot, setSavedSnapshot] = usePersistedState('blendCalculator.savedSnapshot', null);
  // Only populated once someone tries to save or export — flagging empty fields
  // before they have done anything is nagging rather than helping.
  const [missing, setMissing] = useState([]);

  /*
   * On a laptop — where reps use this — three columns leave the grid too
   * narrow, so the customer details (filled in once, then left) start tucked
   * away behind a pull-out tab and slide in on demand, pushing the grid and
   * analysis left rather than covering them. A wider screen keeps all three.
   * When Save or Share is refused for a field that lives there, closing that
   * message opens it.
   */
  const [compactLayout, setCompactLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(LAPTOP_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(LAPTOP_QUERY);
    const onChange = (e) => setCompactLayout(e.matches);
    setCompactLayout(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsShown = !compactLayout || detailsOpen;
  const [customers, setCustomers] = useState([]);
  // A load whose address fallback could not be resolved yet — see the backfill
  // effect below loadSavedBlend.
  const pendingAddressFor = useRef(null);
  const [customersLoading, setCustomersLoading] = useState(true);
  /*
   * The reps a blend may be put on. Only fetched for someone who is not a rep —
   * a rep never sees the field, so there is nothing for them to choose from.
   */
  const [reps, setReps] = useState([]);
  const isRepUser = badgeOf(user) === 'REP';
  useEffect(() => {
    if (isRepUser) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/contacts/sales-reps`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled) setReps(Array.isArray(d) ? d : (d.reps || [])); })
      .catch(() => { /* the field falls back to read-only text */ });
    return () => { cancelled = true; };
  }, [isRepUser]);

  const draft = useBlendDraft(stored.values, stored.locked);

  // Shared with the export modal, and cached, so opening the calculator does not
  // re-fetch ten thousand contacts.
  const [periods, setPeriods] = useState([]);
  useEffect(() => {
    let cancelled = false;
    blendReferenceData()
      .then((ref) => {
        if (cancelled) return;
        setCustomers(ref.customers);
        setPeriods(ref.periods);
      })
      .catch(() => { /* the field stays empty; the save gate reports it */ })
      .finally(() => { if (!cancelled) setCustomersLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /**
   * Customers, told apart by where the lorry goes.
   *
   * Company names repeat across the patch — two "J Smith & Son" rows are the
   * same words and different farms — so the account number and the first line
   * of the delivery address are part of the option rather than something you
   * confirm afterwards by opening the record.
   */
  /*
   * A quote for a farm that is not an account yet.
   *
   * Pinned to the top rather than sorted in, because it is not a customer and
   * would otherwise land under "N". Saving keeps customer_account_id null and
   * sets is_prospect, so "nobody chose" and "quoted for a prospect" stay
   * distinguishable — attaching the blend to an unrelated real customer, which
   * is what reps did before, is worse than an honest blank.
   */
  const customerOptions = useMemo(() => [
    {
      value: PROSPECT,
      label: 'New Prospect',
      badge: 'NEW',
      renderLabel: (
        <span className="bdp-customer-option">
          <span className="bdp-customer-name">New Prospect</span>
          <span className="bdp-customer-where">Not an account yet</span>
        </span>
      ),
      searchText: 'new prospect not an account yet'
    },
    ...customers.map((c) => {
    const where = c.delivery_line1 || c.delivery_city || '';
    return {
      value: String(c.id),
      label: c.company_name,
      badge: c.account_no || '',
      renderLabel: (
        <span className="bdp-customer-option">
          <span className="bdp-customer-name">{c.company_name}</span>
          {where && <span className="bdp-customer-where">{where}</span>}
        </span>
      ),
      searchText: `${c.account_no || ''} ${c.company_name} ${where} ${c.delivery_postcode || ''}`
    };
  })], [customers]);

  const customerById = useMemo(
    () => new Map(customers.map((c) => [String(c.id), c])), [customers]);

  /**
   * Their delivery address, as the address field wants it.
   *
   * Built from the delivery lines rather than the free-text `address` column,
   * which holds the invoice address for most accounts — quoting a blend to the
   * accounts office rather than the farm is the sort of error nobody spots
   * until the lorry is out.
   */
  /* key -> name, for anywhere a material has to be NAMED rather than keyed. */
  const materialNameMap = useMemo(() => Object.fromEntries(
    (book?.materials || []).map((m) => [m.key, m.name])), [book]);

  const addressFor = useCallback((customerId) => {
    const c = customerById.get(String(customerId));
    if (!c) return '';
    return [c.delivery_line1, c.delivery_line2, c.delivery_line3, c.delivery_line4,
            c.delivery_city, c.delivery_postcode]
      .map((p) => String(p || '').trim())
      .filter(Boolean)
      .join(', ');
  }, [customerById]);

  /**
   * Picking a customer fills the address; changing customer replaces it.
   *
   * The field was left for the user to type, so it sat empty on every blend
   * and the export carried no address at all. Only an address that is still
   * the previous customer's — or empty — is overwritten: an edited one is the
   * user's, and a delivery address is exactly the field people correct.
   */
  const handleDetails = useCallback((next) => {
    setDetails((prev) => {
      if (next.customerId === prev.customerId) return next;
      const previousAuto = addressFor(prev.customerId);
      const current = String(prev.deliveryAddressLine || '').trim();
      const untouched = !current || current === previousAuto;
      return untouched
        ? { ...next, deliveryAddressLine: addressFor(next.customerId) }
        : next;
    });
  }, [addressFor, setDetails]);


  // Mirror the working draft into session storage on every change. Only the
  // present state is persisted — undo history would bloat it for little value.
  useEffect(() => {
    setStored({ values: draft.values, locked: draft.locked });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.values, draft.locked]);

  /*
   * cache: 'no-store' throughout.
   *
   * Switching to the Materials tab unmounts this component, so coming back
   * remounts it and refetches — but a plain GET with no Cache-Control and an
   * ETag is left to the browser's heuristics, and the answer was that a
   * mapping or a price edited one tab across did not show until the page was
   * reloaded. There is nothing to gain from caching these: the whole point of
   * returning to the calculator is to work with what was just changed.
   */
  const loadBook = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bookRes, specRes] = await Promise.all([
        fetch(`${API_URL}/api/blends/book?asOf=${asOf}`, { credentials: 'include', cache: 'no-store' }),
        fetch(`${API_URL}/api/blends/specs`, { credentials: 'include', cache: 'no-store' })
      ]);

      if (bookRes.status === 403) {
        // The cost book is separately permissioned. Without it the calculator
        // can still compute an analysis, so fall back rather than dead-ending.
        const bare = await fetch(`${API_URL}/api/blends/materials`,
                                 { credentials: 'include', cache: 'no-store' });
        if (!bare.ok) throw new Error('You do not have access to the blend calculator');
        const data = await bare.json();
        setBook({ materials: data.materials, columns: [], costs: [], nutrients: [],
                  nutrient_components: [], haulage_bands: [], freshness: [], costsHidden: true });
      } else {
        if (!bookRes.ok) throw new Error((await bookRes.json()).error || 'Failed to load the cost book');
        setBook(await bookRes.json());
      }

      if (specRes.ok) setSpecs((await specRes.json()).specs || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => { loadBook(); }, [loadBook]);

  // Default to the first price column once the book arrives.
  useEffect(() => {
    if (!priceColumnKey && book?.columns?.length) setPriceColumnKey(book.columns[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book]);

  // buildBook attaches each material's resolved costs and nutrients, which is
  // what the grid renders per row.
  const computed = useMemo(() => {
    if (!book) return null;
    const built = blendMath.buildBook(
      book.materials, book.costs, book.nutrients, book.columns, book.nutrient_components);
    return { built, materials: Array.from(built.materials.values()) };
  }, [book]);

  const spec = useMemo(
    () => specs.find((s) => s.id === Number(specId)) || null, [specs, specId]);

  /**
   * Only show nutrients something can actually report.
   *
   * The registry carries nine fresh-basis analysis components, but the material
   * data seeded from the workbook covers five. The other four rendered as a
   * permanent row of "—" tiles and four dead grid columns. A nutrient the spec
   * targets stays visible even with no data, because "unknown" against a target
   * is information; a nutrient nobody measures and nobody asked for is noise.
   */
  const visibleNutrients = useMemo(() => {
    if (!computed) return [];
    const targeted = new Set(Object.keys(spec?.targets || {}));
    return book.nutrient_components.filter((c) =>
      targeted.has(c.key) || computed.materials.some((m) => m.nutrients[c.key] !== undefined));
  }, [computed, book, spec]);

  const [comparing, setComparing] = useState(false);
  const [compareChoice, setCompareChoice] = useState('');
  // The fixed side: a saved blend's recipe, held still while the live one is
  // edited. Never written to — only the grid's blend can be changed.
  const [compareBlend, setCompareBlend] = useState(null);

  /**
   * The order the grid lays its rows out in — the material master's sort_order,
   * plus anything in either blend that the book no longer carries.
   *
   * Passed to the proportional line so its sections run in the same sequence as
   * the rows, which is what makes a colour findable: you see a section, you look
   * down the table in the same order and there it is.
   */
  const gridOrder = useMemo(() => {
    if (!computed) return [];
    const known = computed.materials.map((m) => m.key);
    const seen = new Set(known);
    const extras = [
      ...Object.keys(draft.values),
      ...Object.keys(compareBlend?.material_values || {})
    ].filter((k) => !seen.has(k) && seen.add(k));
    return [...known, ...extras];
  }, [computed, draft.values, compareBlend]);

  const summary = useMemo(() => {
    if (!computed) return null;
    return blendMath.summary(draft.values, computed.built, book.haulage_bands, { spec });
  }, [computed, draft.values, book, spec]);

  /**
   * The reference side's figures, computed the same way and on the same date as
   * the live one. Costing two blends on different price dates would make the
   * difference meaningless, so both use the same asOf and the same book.
   */
  const compareSummary = useMemo(() => {
    if (!comparing || !compareBlend || !computed) return null;
    return blendMath.summary(
      compareBlend.material_values, computed.built, book.haulage_bands, { spec });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparing, compareBlend, computed, book, spec]);

  /**
   * What is stopping this blend being saved or exported.
   *
   * Customer is required for both, so every saved blend has an owner. A price
   * basis is only needed to export, since a blend gets costed before anyone
   * decides how it will be quoted.
   */
  const validate = useCallback((forExport) => {
    const gaps = [];
    if (!details.customerId) gaps.push('customerId');
    if (!summary?.materialCount) gaps.push('materials');
    if (forExport && !(details.priceColumnKeys || []).length) gaps.push('priceColumnKeys');
    return gaps;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, summary]);

  // Computed once per render for the tooltips, so the button can explain itself
  // before it is clicked.
  const periodOptions = useMemo(() => periodOptionsFrom(periods), [periods]);

  // ─── What is loaded, and has it been touched ──────────────────────────────
  const contextKind = openBlend ? 'blend' : openFormulation ? 'formulation' : 'draft';
  // The value the B dropdown should be sitting on, so it displays what is
  // loaded rather than a placeholder.
  const loadedValue = openBlend ? `blend:${openBlend.id}`
    : openFormulation ? `formulation:${openFormulation.id}` : '';
  const contextLabel = openBlend
    ? openBlend.name
    : openFormulation
      ? `${openFormulation.item_code} · ${openFormulation.item_name}`
      : 'New blend (unsaved)';

  // A draft with nothing in it is not "changed"; it is empty.
  const isDirty = useMemo(() => {
    if (!summary?.materialCount) return false;
    if (!savedSnapshot) return contextKind !== 'draft' ? true : true;
    return savedSnapshot !== snapshotOf(draft.values, details);
  }, [savedSnapshot, draft.values, details, summary, contextKind]);

  /**
   * Whose recipe is this?
   *
   * An item code starting 100 is a Mole Valley Feed Solutions product, and the
   * raw material costs in this calculator ARE MVFS supplier costs — so an MVFS
   * formulation prices directly and correctly. Anything else is another
   * supplier's recipe written in another supplier's materials: the inclusions
   * are real but the costs against them would be somebody else's, which is a
   * priced quote that means nothing.
   *
   * So a non-MVFS formulation is comparison-only. It goes in B, where it is a
   * reference to build against, and the way to get a price out of it is to
   * convert it — see BlendConvertModal.
   */
  const isMvfsCode = (code) => String(code || '').startsWith('100');

  const [converting, setConverting] = useState(false);
  /*
   * Set once A has been rebuilt from B's recipe. The loud notice above the
   * comparison has done its job at that point — it exists to say "this cannot
   * be priced, here is what to do about it", and both halves stop being true.
   * What remains true is that A's materials are OUR stand-ins for somebody
   * else's, which is a caveat on the analysis rather than a call to action, so
   * it moves to the analysis column and stays there.
   */
  const [rebuiltFrom, setRebuiltFrom] = useState(null);
  const [savedBlends, setSavedBlends] = useState([]);
  const [formulations, setFormulations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [b, f] = await Promise.all([
          // /api/blends has no GET '/' — the saved-blend list lives on /search.
          // The wrong path 404'd, r.ok was false, and the picker silently showed
          // nothing rather than reporting a failure.
          fetch(`${API_URL}/api/blends/search?limit=500`,
                { credentials: 'include', cache: 'no-store' })
            .then((r) => (r.ok ? r.json() : { blends: [] })),
          // Blend-scope formulations only: a compound's recipe is not something
          // the blend calculator can work with.
          // /search has no scope filter, so ask for everything and narrow here.
          /*
           * period=all, explicitly.
           *
           * The search endpoint treats an ABSENT period as "the current one" —
           * right for the Formulations screen, wrong here. This picker is
           * "start a blend from an existing recipe", and the recipe somebody
           * wants is usually the one they built last winter. Without this a rep
           * looking for a customer's November blend saw nothing, because the
           * current period is Summer 2026 and only 77 of the 692 blend
           * formulations sit in it.
           *
           * limit is above the 692 that exist so the list is the whole set
           * rather than a page of it.
           */
          fetch(`${API_URL}/api/formulations/search?period=all&limit=2000`,
                { credentials: 'include', cache: 'no-store' })
            .then((r) => (r.ok ? r.json() : { formulations: [] }))
        ]);
        if (cancelled) return;
        setSavedBlends(b.blends || []);
        // scope is the item's product range; the MVFS split is a separate
        // question and is applied where the options are built.
        setFormulations((f.formulations || []).filter((x) => x.scope === 'blend'));
      } catch (e) {
        if (!cancelled) setError(`Could not load the blend and formulation lists: ${e.message}`);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /** Put a saved blend on the bench, replacing whatever is there. */
  const loadSavedBlend = useCallback(async (id, openNotice, versionId = null) => {
    try {
      const res = await fetch(`${API_URL}/api/blends/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load that blend');
      const b = data.blend || {};
      /*
       * versionId opens an OLDER saved version — the history modal's "open the
       * recipe as it was before this change". The header fields still come from
       * the blend record, because only the recipe is versioned; the quote-side
       * fields have no history of their own beyond the audit log.
       *
       * Falls back to the newest version rather than erroring if the id is not
       * found: a stale id should open the blend, not refuse to.
       */
      const versions = data.versions || [];
      const v = (versionId
        ? versions.find((x) => String(x.id) === String(versionId))
        : null) || versions[0] || {};
      // load(), not replaceValues(): opening a different saved blend is a
      // discontinuity, and undoing back into the previous blend's recipe would
      // be nonsense. The saved-list path always did this; the picker did not.
      draft.load(v.material_values || {}, v.locked_keys || []);
      setRebuiltFrom(null);
      const nextDetails = {
        customerId: b.is_prospect ? PROSPECT
          : (b.customer_account_id ? String(b.customer_account_id) : ''),
        // Blends saved before the address was filled in automatically have none
        // stored; fall back to the customer's rather than loading a blank field
        // over a customer who plainly has an address.
        deliveryAddressLine: b.delivery_address_line
          || addressFor(b.customer_account_id) || '',
        priceColumnKeys: b.price_column_keys || [],
        haulageBandKey: b.haulage_band_key || '',
        periodOfContract: b.period_of_contract || '',
        packaging: b.packaging || '',
        deliveryTerms: b.delivery_terms || '',
        tonnesContracted: b.tonnes_contracted ?? '',
        // numeric(12,2) comes back as a string; the ComboBox compares by value
        // so it has to be the same shape the options carry.
        salePrice: b.sale_price != null ? String(b.sale_price) : '',
        repId: b.rep_id != null ? String(b.rep_id) : '',
        salePriceBasis: b.sale_price_basis || '',
        salePriceType: b.sale_price_type || '',
        notes: b.notes || ''
      };
      const values = v.material_values || {};
      const snapshot = snapshotOf(values, nextDetails);
      // Opening straight from the Saved Blends tab mounts this component and
      // fetches the customer list at the same time as the blend, so a blend
      // with no stored address can land before addressFor() can answer. Note
      // what is needed to finish the job, rather than leaving a customer who
      // plainly has an address showing none.
      pendingAddressFor.current =
        (b.customer_account_id && !nextDetails.deliveryAddressLine)
          ? { customerId: b.customer_account_id, values, details: nextDetails, snapshot }
          : null;
      setDetails(nextDetails);
      // The date the blend was priced at. Without this the bench reopens at
      // today's raw material costs, so a blend saved in November reprices
      // itself on load and the figures no longer match what was quoted.
      if (v.priced_as_of) setAsOf(v.priced_as_of);
      setOpenBlend({ id: b.id, name: b.name, blend_type: b.blend_type || '' });
      // Restore the formulation this blend was built from, rather than
      // clearing it: openFormulation is what supplies source_formulation_id on
      // the next save, so nulling it here made every re-save of a loaded blend
      // drop the link back to its formulation. openBlend still wins for the
      // header, so this changes what is SAVED, not what is shown.
      setOpenFormulation(b.source_formulation_id
        ? {
            formulation_id: b.source_formulation_id,
            item_code: b.source_formulation_code || `#${b.source_formulation_id}`,
            item_name: b.source_formulation_name || 'Formulation'
          }
        : null);
      setSavedSnapshot(snapshot);
      setError(null);
      setNotice(openNotice || `Loaded "${b.name}".`);
    } catch (e) { setError(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, addressFor]);

  /*
   * Fill the address a load could not resolve, once the customer list is in.
   *
   * Guarded by a ref set during the load and cleared the moment it is used, so
   * this fires once for that blend and never afterwards — a user who clears the
   * address field on purpose does not have it typed back at them.
   */
  useEffect(() => {
    const pending = pendingAddressFor.current;
    if (!pending) return;
    const address = addressFor(pending.customerId);
    if (!address) return;
    pendingAddressFor.current = null;
    setDetails((d) => (d.deliveryAddressLine ? d : { ...d, deliveryAddressLine: address }));
    // The baseline moves with it. The fallback address is what the picker path
    // has always opened with, so a blend that has just been opened must not
    // read as edited the moment the customer list lands.
    setSavedSnapshot((s) => (s === pending.snapshot
      ? snapshotOf(pending.values, { ...pending.details, deliveryAddressLine: address })
      : s));
    // setDetails/setSavedSnapshot come from usePersistedState, which returns
    // React's own useState setters — stable, though eslint cannot see that
    // through the custom hook. Both are used functionally regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressFor]);

  /**
   * Start from an existing formulation.
   *
   * One-way on purpose: a formulation's recipe seeds a blend, but the blend does
   * not write back. The quote-side fields have no equivalent on a formulation,
   * so they stay blank for the user to fill in — which is why this opens as an
   * unsaved draft rather than pretending to be a saved blend.
   *
   * The component keys of a formulation are the same material keys the
   * calculator uses, so the values transfer directly; anything the material
   * master no longer knows about is dropped by the server on save.
   */
  const loadFormulation = useCallback(async (formulationId) => {
    try {
      // The server does the mapping: a formulation stores component keys, the
      // calculator works in blend materials, and pricing.blend_material_components
      // maps between them. Doing it here would mean shipping that table to the
      // browser to answer one question.
      const res = await fetch(`${API_URL}/api/formulations/${formulationId}/as-blend`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load that formulation');

      const values = data.material_values || {};
      if (!Object.keys(values).length) {
        setError('Nothing in that formulation maps to a blend material, so there is nothing to load.');
        return;
      }

      const meta = formulations.find((f) => String(f.id) === String(formulationId)) || {};
      draft.replaceValues(values, []);
      setRebuiltFrom(null);
      setOpenBlend(null);
      setOpenFormulation({
        formulation_id: formulationId,
        item_code: meta.item_code || `#${formulationId}`,
        item_name: meta.item_name || 'Formulation'
      });
      // Deliberately not snapshotted as saved: nothing has been saved yet, so
      // the header reads as an unsaved blend from the moment it loads. Saving
      // creates a NEW blend and never writes back to the formulation.
      setSavedSnapshot(null);

      const loaded = Object.keys(values).length;
      if (data.unmapped?.length) {
        // Named rather than counted: "3 of 5 loaded" leaves the user to work out
        // which two are missing from a recipe they may not know by heart.
        setError(`Loaded ${loaded} material${loaded === 1 ? '' : 's'}. Not loaded: `
          + data.unmapped.map((u) => `${u.label} (${u.reason})`).join(', ')
          + '. Add them by hand before saving.');
      } else {
        setError(null);
        setNotice(`Started a blend from ${meta.item_code || 'that formulation'}. Fill in the customer and pricing, then save.`);
      }
    } catch (e) { setError(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, formulations]);

  /* Declared above startNewBlend, which calls it: a deps array is evaluated
     during render, so a const declared further down would be in its temporal
     dead zone and throw. */
  const stopComparing = useCallback(() => {
    setComparing(false);
    setCompareChoice('');
    setCompareBlend(null);
  }, []);

  const startNewBlend = useCallback(() => {
    draft.clear();
    setOpenBlend(null);
    setOpenFormulation(null);
    setSavedSnapshot(null);
    setRebuiltFrom(null);
    // B as well. Starting a new blend against the last one's reference is not a
    // new blend — and the A/B columns, second lines and paired figures would
    // stay on screen for a comparison the user has just cleared the other half
    // of. stopComparing clears the choice, the flag and the loaded reference.
    stopComparing();
    setDetails({
      customerId: '', deliveryAddressLine: '', priceColumnKeys: [], haulageBandKey: '',
      periodOfContract: '', packaging: '', deliveryTerms: '', tonnesContracted: '',
      // Everything added since this was written. A reset that leaves the last
      // blend's sale price and rep behind is not a new blend.
      salePrice: '', salePriceBasis: '', salePriceType: '', repId: '', notes: ''
    });
    setMissing([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, stopComparing]);

  /**
   * One list to load from.
   *
   * A saved blend and a formulation are both "something that already exists
   * that I want on the bench"; which of our two tables it came out of is our
   * problem, not the user's. Values are prefixed so the handler knows which to
   * fetch, and the group is carried through so the distinction still shows
   * where it matters.
   */
  const loadOptions = useMemo(() => [
    ...savedBlends.map((b) => ({
      value: `blend:${b.id}`,
      label: b.name,
      // The customer is what tells two "Nov 25 Blend" rows apart, so it travels
      // as a second line rather than being run into the name with a dash.
      renderLabel: (
        <span className="bwb-option">
          <span className="bwb-option-name">{b.name}</span>
          {b.customer_name && <span className="bwb-option-sub">{b.customer_name}</span>}
        </span>
      ),
      group: 'Saved blends',
      /* Also findable by who it is for and where they are: a farm is
         routinely known by its address rather than the account's registered
         name, and by its account number rather than either. */
      searchText: [b.name, b.customer_name, b.customer_account_no,
                   b.customer_address_line, b.blend_type].filter(Boolean).join(' ')
    })),
    ...formulations.filter((f) => isMvfsCode(f.item_code)).map((f) => formulationOption(f))
  ], [savedBlends, formulations]);

  /** One formulation, as a picker row. */
  function formulationOption(f, { external = false } = {}) {
    return {
      value: `formulation:${f.id}`,
      label: f.item_name,
      // The item code is an identifier, so it gets the badge every other
      // identifier in the system gets rather than being punctuation in a string.
      badge: f.item_code,
      renderLabel: (
        <span className="bwb-option">
          <span className="bwb-option-name">
            {f.item_name}
            {external && <span className="bwb-option-flag">Not MVFS</span>}
          </span>
          {f.customer_blend_name && (
            <span className="bwb-option-sub">{f.customer_blend_name}</span>
          )}
        </span>
      ),
      group: external ? 'Other suppliers — compare only' : 'Formulations',
      searchText: [f.item_code, f.item_name, f.customer_blend_name,
                   f.customer_blend_account, f.customer_blend_account_no,
                   f.customer_address_line].filter(Boolean).join(' ')
    };
  }

  /**
   * The same list to compare against, plus a blank.
   *
   * Holding a recipe beside one built from nothing — "what would this cost if
   * we started again" — is the most common comparison there is, and needing a
   * saved blend on both sides made it the one thing you could not do.
   */
  /**
   * What B can hold.
   *
   * The first row turns the comparison OFF. Comparing is the exception rather
   * than the default — most of the time somebody is building one blend and every
   * A/B column, second line and paired figure is noise — so there has to be a
   * way back to a single blend that is as easy as the way in.
   */
  const compareOptions = useMemo(() => [
    { value: '', label: 'Not comparing', group: 'Off',
      searchText: 'none off clear stop single' },
    { value: BLANK_COMPARE, label: 'A blank blend', group: 'Start from nothing',
      searchText: 'blank empty scratch new nothing' },
    ...loadOptions.map((o) => ({ ...o, value: `cmp:${o.value}` })),
    /*
     * The non-MVFS formulations, which appear HERE and nowhere else. Loading
     * one into A would put another supplier's materials on the bench and price
     * them against MVFS costs, which is a number that looks right and is not.
     * As B it is what it actually is: a recipe to build something equivalent
     * against.
     */
    ...formulations.filter((f) => !isMvfsCode(f.item_code))
      .map((f) => ({ ...formulationOption(f, { external: true }),
                     value: `cmp:formulation:${f.id}` }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [loadOptions, formulations]);


  /** One prefixed value in, the right fetch out. */
  const handleLoad = useCallback((value) => {
    const [kind, id] = String(value).split(':');
    if (kind === 'blend') loadSavedBlend(id);
    else if (kind === 'formulation') loadFormulation(id);
  }, [loadSavedBlend, loadFormulation]);

  /**
   * Pick what to compare against.
   *
   * A blank is a real choice, not an empty one: an empty reference is exactly
   * what you want when the question is "what would this cost built from
   * scratch": every material shows a 0 against A and its real figure against B.
   */
  const handleCompare = useCallback(async (value) => {
    setCompareChoice(value || '');
    // Picking a different B (or none) makes the standing caveat about the old
    // one meaningless — A was rebuilt from a recipe that is no longer on screen.
    setRebuiltFrom(null);
    if (!value) { stopComparing(); return; }

    if (value === BLANK_COMPARE) {
      setComparing(true);
      setCompareBlend({ id: null, name: 'A blank blend', material_values: {} });
      return;
    }

    const [, kind, id] = String(value).split(':');
    try {
      if (kind === 'blend') {
        const res = await fetch(`${API_URL}/api/blends/${id}`, { credentials: 'include' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load that blend');
        const v = (data.versions || [])[0] || {};
        setCompareBlend({
          id: data.blend?.id,
          name: data.blend?.name,
          material_values: v.material_values || {}
        });
      } else {
        const res = await fetch(`${API_URL}/api/formulations/${id}/as-blend`,
                                { credentials: 'include' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load that formulation');
        const meta = formulations.find((f) => String(f.id) === String(id)) || {};
        setCompareBlend({
          id: null,
          formulation_id: Number(id),
          name: meta.item_name || data.name || 'Formulation',
          item_code: meta.item_code || '',
          // Not one of ours: the recipe is another supplier's, so its costs are
          // not the costs in this book. Carried on the object so the panel can
          // say so and offer the conversion.
          external: !isMvfsCode(meta.item_code),
          // Every ingredient line with what it could map to, for the conversion.
          components: data.components || [],
          material_values: data.material_values || {}
        });
      }
      setComparing(true);
    } catch (e) { setError(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopComparing, formulations]);

  const saveGaps = useMemo(() => validate(false), [validate]);
  const exportGaps = useMemo(() => validate(true), [validate]);

  const gateMessage = (gaps) => {
    if (gaps.includes('materials')) return 'Add a material first.';
    if (gaps.includes('customerId')) return 'Pick the customer this blend is for.';
    if (gaps.includes('priceColumnKeys')) return 'Tick at least one price basis.';
    return null;
  };

  // A blend opened from the Saved Blends tab replaces the draft. load() clears
  // the undo history too — undoing back into a different blend's recipe would
  // be nonsense.
  useEffect(() => {
    if (!loadBlend) return;
    /*
     * The SAME fetch the in-calculator picker uses, rather than restoring from
     * the prop.
     *
     * `loadBlend` is a row from the saved-blends LIST, and a list row carries
     * the recipe but none of the quote-side fields — customer, notes, tonnes,
     * packaging, delivery terms, period, sale price. Restoring from it put the
     * materials on the bench and left every one of those showing whatever the
     * previous blend had left there, which reads as the blend having been saved
     * without them.
     *
     * Going through loadSavedBlend() also sets openBlend from the fetched
     * record and takes the savedSnapshot, so "unsaved changes" is measured
     * against what is actually stored.
     */
    loadSavedBlend(
      loadBlend.id,
      loadBlend.versionId
        ? `Opened "${loadBlend.name}" as it was saved earlier. Saving adds a new version — it does not rewrite history.`
        : `Opened "${loadBlend.name}". Saving will offer to update it or keep a separate copy.`,
      loadBlend.versionId || null
    );
    if (onLoaded) onLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadBlend]);

  const saveBlend = useCallback(async ({ mode, id, name, blend_type: blendType, notes }) => {
    setSavingBlend(true);
    setSaveError(null);
    try {
      const res = await fetch(
        mode === 'update' && id ? `${API_URL}/api/blends/${id}` : `${API_URL}/api/blends`,
        {
          method: mode === 'update' && id ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name,
            blend_type: blendType,
            material_values: draft.values,
            locked_keys: draft.locked,
            priced_as_of: asOf,
            // Everything the details panel collects travels with the blend, so
            // it is not re-typed at export time by whoever happens to send it.
            customer_account_id: details.customerId && details.customerId !== PROSPECT
              ? Number(details.customerId) : null,
            is_prospect: details.customerId === PROSPECT,
            /*
             * Only meaningful where the account cannot answer — the server
             * takes the account's rep in front of this one either way. '' so a
             * rep chosen while the blend was a prospect is cleared when a real
             * customer is picked.
             */
            rep_id: details.repId || '',
            delivery_address_line: details.deliveryAddressLine || null,
            delivery_terms: details.deliveryTerms || null,
            packaging: details.packaging || null,
            tonnes_contracted: details.tonnesContracted || null,
            period_of_contract: details.periodOfContract || null,
            haulage_band_key: details.haulageBandKey || null,
            price_column_keys: details.priceColumnKeys || [],
            source_formulation_id: openFormulation?.formulation_id || null,
            /*
             * '' rather than null for a cleared price, and the server treats
             * the two differently on update: '' clears the column, null leaves
             * whatever is stored. Sending null here meant a price could be
             * added but never removed — the field went blank on screen and the
             * old figure was still in the database.
             */
            sale_price: details.salePrice === '' || details.salePrice == null
              ? '' : String(Number(details.salePrice)),
            // What that figure was based on. Kept even when the price was typed
            // rather than picked — a rounded quote is still quoted on a basis.
            sale_price_basis: details.salePriceBasis || '',
            sale_price_type: details.salePriceType || '',
            /*
             * The blend's own notes are the ones written in the details panel —
             * they describe the blend and are what the feed desk reads in the
             * saved-blend email.
             *
             * The save dialog's note is a different thing: "what changed, or
             * why", about this version. It used to win here, so anyone who
             * typed one silently replaced the blend's notes and the email went
             * out describing the edit instead of the blend.
             */
            notes: details.notes || null,
            version_notes: notes || null
          })
        });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');

      // A save-as-new becomes the open blend, so the next save offers to update
      // the copy rather than spawning another one.
      const saved = data.blend || {};
      setOpenBlend({
        id: saved.id ?? id,
        name: saved.name ?? name,
        blend_type: saved.blend_type ?? blendType ?? ''
      });
      setShowSave(false);
      setNotice(data.unchanged
        ? `"${name}" is unchanged — no new version was created.`
        : (mode === 'update' ? `Updated "${name}".` : `Saved "${name}".`));
      // Marks the current recipe as the saved one, so the unsaved-changes badge
      // clears until the next edit.
      setSavedSnapshot(snapshotOf(draft.values, details));
      // Offered, not automatic: not every save is worth an email, and a blend
      // often gets saved several times while it is being worked out.
      if (!data.unchanged) setNotifyFor({ name, id: data.blend?.id ?? id });
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSavingBlend(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.values, draft.locked, asOf, details, openFormulation]);

  /**
   * Tell the feed desk a blend has been saved.
   *
   * Sent from the person who saved it, to feed@ — so the desk can see who to
   * ask. It used to go from feed@ to itself. The rule is in routes/blends.js.
   */
  const notifyFeed = useCallback(async (blend) => {
    try {
      const res = await fetch(`${API_URL}/api/blends/${blend.id}/notify`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priced_as_of: asOf })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setNotifyFor(null);
      setNotice(data.specAttached === false && data.specNote
        ? `"${blend.name}" saved, and feed@ has been notified. ${data.specNote}`
        : `"${blend.name}" saved, and feed@ has been sent it with the supplier specification attached.`);
    } catch (e) {
      setNotifyFor(null);
      setError(`Saved, but the notification could not be sent: ${e.message}`);
    }
  }, [asOf]);

  // Undo/redo. preventDefault only when there is something to undo, so the
  // browser's own undo still works in every other field on the page.
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || e.key.toLowerCase() !== 'z') return;
      if (e.shiftKey) {
        if (!draft.canRedo) return;
        e.preventDefault(); draft.redo();
      } else {
        if (!draft.canUndo) return;
        e.preventDefault(); draft.undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [draft]);

  /** Resolve pasted material names against the master list and its aliases. */
  const handlePasteNames = useCallback(async (payload) => {
    if (payload.overflow) {
      setError(`${payload.overflow} pasted value(s) had no row to land in.`);
      return;
    }
    if (!payload.pairs?.length) return;
    try {
      const res = await fetch(`${API_URL}/api/blends/materials/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ names: payload.pairs.map((p) => p.name) })
      });
      const data = await res.json();
      setPaste({ pairs: payload.pairs, resolved: data.resolved || {} });
    } catch (e) {
      setError('Could not resolve the pasted material names');
    }
  }, []);

  const applyPaste = useCallback((mapped) => {
    const next = { ...draft.values };
    for (const row of mapped) if (row.materialKey) next[row.materialKey] = row.value;
    draft.replaceValues(next);
    setPaste(null);
  }, [draft]);

  useSlideOutButton([draft.canUndo, draft.canRedo, summary?.isBalanced, showAll, summary?.materialCount, savingBlend]);

  const specOptions = useMemo(() => [
    { value: '', label: 'No target spec' },
    ...specs.map((s) => ({ value: String(s.id), label: s.name }))
  ], [specs]);

  /**
   * Everything the calculator can do, as a list.
   *
   * Built here rather than inside the palette because the palette should not
   * know what a blend is — it filters and runs, and the screen decides what
   * there is to run. Disabled commands carry their reason, so the palette can
   * say why rather than quietly omitting them.
   */

  /**
   * What a brief can name: saved blends, formulations, and the customers behind
   * them. Assembled from lists already loaded for the pickers, so recognising
   * "Chapple's November blend" costs nothing extra.
   */
  const briefCatalogue = useMemo(() => ({
    blends: savedBlends.map((b) => ({ id: b.id, name: b.name, customer_name: b.customer_name })),
    formulations: formulations.map((f) => ({
      id: f.id, item_code: f.item_code, item_name: f.item_name,
      customer_blend_name: f.customer_blend_name
    })),
    customers: customers.map((c) => ({ id: c.id, company_name: c.company_name }))
  }), [savedBlends, formulations, customers]);

  /** The recipe behind a chosen starting point, whichever kind it is. */
  const loadBaseValues = useCallback(async (base) => {
    if (!base) return null;
    const url = base.kind === 'formulation'
      ? `${API_URL}/api/formulations/${base.id}/as-blend`
      : `${API_URL}/api/blends/${base.id}`;
    const res = await fetch(url, { credentials: 'include' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Could not load that blend');
    return base.kind === 'formulation'
      ? (data.material_values || {})
      : ((data.versions || [])[0]?.material_values || {});
  }, []);

  /*
   * ⌘K / Ctrl-K opens "build me a blend".
   *
   * It used to open the command palette, which offered the brief among a list
   * of other commands — a menu in front of the one thing anybody wanted from
   * it. The shortcut now goes straight there. Both modifiers are accepted so it
   * works on either kind of machine; only the BADGE has to choose, which is
   * what shortcutLabel is for.
   *
   * Capture phase, so a focused grid cell does not swallow it first.
   */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setBriefing((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  return (
    <div className="search-layout blend-calculator" style={{ zIndex: 2 }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Blend Calculator</span>
          {/* Short on purpose. A paragraph explaining the gestures sat above
              the work all day for the sake of the first ten minutes. */}
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>Type an inclusion % against each material.</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout bold">
            <div>
              <button className="btn-slideout" onClick={draft.undo} disabled={!draft.canUndo}
                      title={draft.canUndo ? 'Undo' : 'Nothing to undo'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">undo</span></div>
                <span className="btn-label">Undo</span>
              </button>
            </div>
            <div style={{ marginRight: '15px' }}>
              <button className="btn-slideout" onClick={draft.redo} disabled={!draft.canRedo}
                      title={draft.canRedo ? 'Redo' : 'Nothing to redo'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">redo</span></div>
                <span className="btn-label">Redo</span>
              </button>
            </div>
            <div>
              <button data-read-safe className="btn-slideout btn-danger"
                      onClick={() => setConfirmReset(true)}
                      disabled={!Object.keys(draft.values).length}
                      title="Clear the bench and start a new blend">
                <div className="icon-wrapper"><span className="material-symbols-rounded">cancel</span></div>
                <span className="btn-label">Reset</span>
              </button>
            </div>
            <div style={{ marginRight: '15px' }}>
              <button className="btn-slideout"
                      onClick={() => {
                        const gaps = validate(false);
                        setMissing(gaps);
                        if (gaps.length) { setError(gateMessage(gaps)); return; }
                        setSaveError(null); setShowSave(true);
                      }}
                      disabled={savingBlend}
                      title={saveGaps.length ? gateMessage(saveGaps) : 'Keep this blend for reuse'}>
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded${savingBlend ? ' spinning' : ''}`}>
                    {savingBlend ? 'progress_activity' : 'save'}
                  </span>
                </div>
                <span className="btn-label">Save</span>
              </button>
            </div>
            <div>
              {/* Enabled either way, with the reason in the tooltip and on click.
                  A disabled button that will not say why is the worst of both. */}
              <button className="btn-slideout"
                      onClick={() => {
                        const gaps = validate(true);
                        setMissing(gaps);
                        if (gaps.length) { setError(gateMessage(gaps)); return; }
                        setExporting(true);
                      }}
                      title={exportGaps.length ? gateMessage(exportGaps) : 'Customer quote or supplier specification'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">ios_share</span></div>
                <span className="btn-label">Share</span>
              </button>
            </div>
            {/* Inverted, because it is the only button here that MAKES a blend
                rather than acting on the one already on the bench. */}
            <div style={{ marginLeft: '15px' }}>
              <button className="btn-slideout btn-invert"
                      onClick={() => setBriefing(true)}
                      disabled={!computed}
                      title={computed
                        ? `Describe what you want and have it built (${shortcutLabel('K')})`
                        : 'Waiting for the material prices'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">auto_awesome</span></div>
                <span className="btn-label">Build me a blend</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sits with the comparison it is about, above the bar carrying the B
          picker — not in the floating stack top-right, which is for things that
          have just happened rather than a standing condition of what is loaded.

          Stands down the moment A has been rebuilt: at that point neither half
          of it is true any more. What survives moves to the analysis column. */}
      {comparing && compareBlend?.external && !rebuiltFrom && (
        <div className="blend-external-notice" role="status">
          <p className="ben-text">
            <span className="material-symbols-rounded">swap_horiz</span>
            <span>
              <strong>{compareBlend.item_code || compareBlend.name}</strong> is not a Mole
              Valley formulation, so the costs in this book do not apply to it. Rebuild it
              from our materials to get a price.
            </span>
          </p>
          <button type="button" className="ben-action" onClick={() => setConverting(true)}>
            <span className="material-symbols-rounded">move_down</span>
            Rebuild from our materials
          </button>
        </div>
      )}

      <BlendWorkBar
        contextKind={contextKind}
        contextLabel={contextLabel}
        isDirty={isDirty}
        loadOptions={loadOptions}
        onLoad={handleLoad}
        loadedValue={loadedValue}
        compareOptions={compareOptions}
        compareId={compareChoice}
        onCompare={handleCompare}
        asOf={asOf}
        onAsOf={(e) => setAsOf(e.target.value)}
        specOptions={specOptions}
        specId={specId}
        onSpec={setSpecId}
      />

      <div className="search-content">
        {/* Top-right toast rather than a row in the flow. An inline notice
            pushed the whole workspace down and back up as it came and went,
            which is a jolt in the middle of typing percentages. Inverted
            colours so it reads as a system message over the page rather than
            as part of it. */}
        <div className="blend-toasts" aria-live="polite">
          {notice && !error && (
            <div className="blend-toast" role="status">
              <span className="material-symbols-rounded">check_circle</span>
              <span className="bt-text">{notice}</span>
              <button className="bt-close" onClick={() => setNotice(null)} title="Dismiss">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
          )}
          {error && (
            <div className="blend-toast is-error" role="alert">
              <span className="material-symbols-rounded">error</span>
              <span className="bt-text">{error}</span>
              <button className="bt-close" onClick={() => {
                setError(null);
                // The missing field is in the customer details: show it.
                if (missing.some(g => g === 'customerId' || g === 'priceColumnKeys')) setDetailsOpen(true);
              }} title="Dismiss">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div className="loading">
            <span className="material-symbols-rounded spinning">progress_activity</span>
            <span>Loading the cost book…</span>
          </div>
        )}

        {!loading && computed && (
          <div className={`blend-workspace${compactLayout ? ' is-compact' : ''}${detailsShown ? ' details-open' : ''}`}>
            <div className="blend-grid-column">
              {/* The blend as a shape, above the same values as a table. Coarse
                  moves and the sense of the thing happen here; a 0.25% tweak
                  still belongs in a text field below. */}
              <BlendAllocationBar
                materials={computed.materials}
                draft={draft}
                order={gridOrder}
                compareValues={comparing ? compareBlend?.material_values || null : null}
              />
              <BlendGrid
                materials={computed.materials}
                draft={draft}
                summary={summary}
                priceColumnKey={priceColumnKey}
                nutrientComponents={visibleNutrients}
                showAllMaterials={showAll}
                onToggleShowAll={setShowAll}
                onPasteNames={handlePasteNames}
                onBalance={() => draft.balance(redistributeMode)}
                compareValues={comparing ? compareBlend?.material_values || null : null}
                compareContributions={compareSummary?.pricing?.[priceColumnKey]?.contributions || null}
              />
            </div>
            <BlendSummary
              rebuiltFrom={rebuiltFrom}
              summary={summary}
              columns={book.columns}
              haulageBands={book.haulage_bands}
              nutrientComponents={visibleNutrients}
              priceColumnKey={priceColumnKey}
              onSelectPriceColumn={setPriceColumnKey}
              spec={spec}
              asOf={asOf}
              freshness={book.freshness}
              onBalance={() => draft.balance(redistributeMode)}
              compareSummary={compareSummary}
              compareName={compareBlend?.name}
            />
            {compactLayout && (
              <button
                type="button"
                className={`blend-details-tab${detailsOpen ? ' open' : ''}`}
                onClick={() => setDetailsOpen(o => !o)}
                aria-expanded={detailsOpen}
                title={detailsOpen ? 'Tuck the customer details away' : 'Show the customer details'}
              >
                <span className="material-symbols-rounded">{detailsOpen ? 'chevron_right' : 'chevron_left'}</span>
                <span className="bdt-label">Customer details</span>
                {missing.some(g => g === 'customerId' || g === 'priceColumnKeys') && (
                  <span className="bdt-needs" title="Something here is needed to save">!</span>
                )}
              </button>
            )}
            <div className="blend-details-drawer" aria-hidden={!detailsShown}>
            <BlendDetailsPanel
              details={details}
              onChange={handleDetails}
              customerOptions={customerOptions}
              customersLoading={customersLoading}
              priceColumns={book.columns}
              haulageBands={book.haulage_bands}
              summary={summary}
              periodOptions={periodOptions}
              missing={missing}
              reps={reps}
              isRepUser={isRepUser}
              customers={customers}
            />
            </div>
          </div>
        )}
      </div>

      {briefing && computed && (
        <BlendBriefModal
          materials={computed.materials}
          book={computed.built}
          components={visibleNutrients}
          columnKey={priceColumnKey}
          catalogue={briefCatalogue}
          loadBaseValues={loadBaseValues}
          onClose={() => setBriefing(false)}
          onApply={(option) => {
            draft.replaceValues(option.values, []);
            setNotice(`Built from your brief — ${option.title.toLowerCase()}.`);
          }}
        />
      )}



      {exporting && (
        <BlendExportModal
          materialNames={materialNameMap}
          /*
           * The fixed side of an active comparison, so the export can offer a
           * comparison sheet. Null when not comparing, which is what hides that
           * document type — a "comparison" of one blend against nothing is not a
           * document anybody wants.
           */
          compareBlend={comparing && compareBlend ? {
            name: compareBlend.name || 'Reference',
            blend_type: compareBlend.blend_type || '',
            material_values: compareBlend.material_values,
            sale_price: compareBlend.sale_price ?? null
          } : null}
          blend={{
            name: openBlend?.name || '',
            blend_type: openBlend?.blend_type || '',
            material_values: draft.values,
            // Everything already chosen in the details panel, so the export does
            // not ask a second time for what the blend already knows.
            price_column_keys: details.priceColumnKeys || [],
            haulage_band_key: details.haulageBandKey || '',
            // A prospect is not an account, so nothing is prefilled from one —
            // the export modal asks for a recipient as it would with no
            // customer chosen at all.
            customer_account_id: details.customerId === PROSPECT ? '' : (details.customerId || ''),
            is_prospect: details.customerId === PROSPECT,
            delivery_address_line: details.deliveryAddressLine || '',
            packaging: details.packaging || '',
            delivery_terms: details.deliveryTerms || '',
            tonnes_contracted: details.tonnesContracted || '',
            period_of_contract: details.periodOfContract || '',
            notes: details.notes || ''
          }}
          columns={book.columns}
          bands={book.haulage_bands}
          asOf={asOf}
          summary={summary}
          onClose={() => setExporting(false)}
          onDone={(msg) => { setExporting(false); setError(null); if (msg) setNotice(msg); }}
        />
      )}

      {/* Confirmed, because it is not undoable: clear() resets the draft's
          history as well as its values, so there is nothing to undo back to. */}
      <ConfirmDialog
        isOpen={confirmReset}
        title="Start a new blend?"
        message={isDirty
          ? 'This blend has changes that have not been saved. Starting a new one throws them away, and undo will not bring them back.'
          : 'This clears the grid and the details, and unloads whatever is open. Undo will not bring it back.'}
        confirmText="Start new"
        cancelText="Keep working"
        onConfirm={() => { setConfirmReset(false); startNewBlend(); }}
        onCancel={() => setConfirmReset(false)}
      />

      {/* Offered after a save rather than sent automatically — a blend gets
          saved several times while it is being worked out, and not every save
          is worth an email to the desk. */}
      <ConfirmDialog
        isOpen={!!notifyFor}
        title="Notify the feed desk?"
        message={notifyFor
          ? `Email feed@excel-agri.com with "${notifyFor.name}" — its customer, materials and quantities, with the supplier specification PDF attached?`
          : ''}
        confirmText="Send"
        cancelText="Not now"
        onConfirm={() => notifyFeed(notifyFor)}
        onCancel={() => setNotifyFor(null)}
      />

      {converting && compareBlend?.external && computed && (
        <BlendConvertModal
          source={compareBlend}
          materials={computed.materials}
          priceColumnKey={priceColumnKey}
          onClose={() => setConverting(false)}
          onApply={(values) => {
            setConverting(false);
            // replaceValues, not load(): the user is building INTO the bench
            // they already have open, and undo should take them back to it.
            draft.replaceValues(values, []);
            setOpenBlend(null);
            setOpenFormulation(null);
            setSavedSnapshot(null);
            setRebuiltFrom({
              item_code: compareBlend.item_code || '',
              name: compareBlend.name || ''
            });
            const n = Object.keys(values).length;
            setNotice(`Rebuilt from ${compareBlend.item_code || compareBlend.name} — `
              + `${n} material${n === 1 ? '' : 's'} on the bench. `
              + 'Check the balance and the analysis against B before quoting.');
          }}
        />
      )}

      {showSave && (
        <BlendSaveModal
          openBlend={openBlend}
          saving={savingBlend}
          error={saveError}
          onClose={() => setShowSave(false)}
          onSave={saveBlend}
        />
      )}

      {paste && (
        <BlendPasteModal
          pairs={paste.pairs}
          resolved={paste.resolved}
          materials={computed?.materials || []}
          onCancel={() => setPaste(null)}
          onApply={applyPaste}
        />
      )}
    </div>
  );
}
